import { useEffect, useState } from 'react';

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

export function useMediaQuery(query: string): boolean {
  const get = () =>
    typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState<boolean>(get);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** True when viewport is strictly below the given breakpoint. */
export function useBelowBreakpoint(bp: Breakpoint): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS[bp] - 1}px)`);
}

/** True when viewport is at or above the given breakpoint. */
export function useAboveBreakpoint(bp: Breakpoint): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS[bp]}px)`);
}

/** Convenience: mobile-or-tablet (below md = 768px). */
export const useIsMobile = (): boolean => useBelowBreakpoint('md');

/** Convenience: below the lg threshold where the right sidebar shows. */
export const useIsBelowDesktop = (): boolean => useBelowBreakpoint('lg');
